import React from 'react';
import {
  DataGrid,
  GridCellModes,
  GridCellModesModel,
  GridCellParams,
  GridColDef,
  GridRenderCellParams,
  useGridApiRef,
} from '@mui/x-data-grid';
import {
  darken,
  IconButton,
  lighten,
  Stack,
  styled,
  Theme,
  Typography,
} from '@mui/material';
import { Restore } from '@material-ui/icons';
import { WebstoreProductPart } from '../../models/Product';
import ColorIndicator from '../../components/product/ColorIndicator';

export interface InventoryRow extends WebstoreProductPart {
  index: number;
  originalInventory?: number;
  originalInventoryMinimum?: number;
}

const getBackgroundColor = (color: string, theme: Theme, coefficient: number) => ({
  backgroundColor: darken(color, coefficient),
  ...theme.applyStyles('light', {
    backgroundColor: lighten(color, coefficient),
  }),
});

const StyledDataGrid = styled(
  DataGrid<InventoryRow>,
  {},
)(({ theme }) => ({
  '& .changed': {
    backgroundColor: getBackgroundColor(theme.palette.primary.main, theme, 0.9).backgroundColor,
  },
  '& .changed.MuiDataGrid-row:hover': {
    backgroundColor: getBackgroundColor(theme.palette.primary.main, theme, 0.8).backgroundColor,
  },
  '& .minimum': {
    backgroundColor: getBackgroundColor(theme.palette.error.main, theme, 0.9).backgroundColor,
  },
  '& .minimum.MuiDataGrid-row:hover': {
    backgroundColor: getBackgroundColor(theme.palette.error.main, theme, 0.8).backgroundColor,
  },
}));

export default function AetherInventoryTable({
  parts,
  onChange,
}: {
  parts: InventoryRow[];
  onChange: (index: number, row: InventoryRow) => void;
}) {
  const apiRef = useGridApiRef();
  const [cellModesModel, setCellModesModel] = React.useState<GridCellModesModel>({});

  const handleReset =
    (params: GridRenderCellParams, field: keyof InventoryRow) => (e: React.SyntheticEvent) => {
      // Prevents edit
      e.stopPropagation();
      const updatedRow = {
        ...params.row,
        aetherInventory:
          field === 'aetherInventory' ? params.row.originalInventory : params.row.aetherInventory,
        aetherInventoryMinimum:
          field === 'aetherInventoryMinimum'
            ? params.row.originalInventoryMinimum
            : params.row.aetherInventoryMinimum,
        _action: 'update',
      };
      apiRef.current?.updateRows([updatedRow]);
      setCellModesModel((prevModel) => ({
        ...prevModel,
        [params.id]: {
          ...prevModel[params.id],
          aetherInventory: { mode: GridCellModes.View },
        },
      }));
      onChange(params.row.index, updatedRow);
    };

  const columns: GridColDef[] = [
    { field: 'sku', headerName: 'Sku', flex: 1 },
    {
      field: 'color',
      headerName: 'Color',
      flex: 1,
      valueGetter: (_, row) => row.ColorArray.Color.colorName,
      renderCell: (params) => (
        <Stack direction={'row'} spacing={1} alignItems={'center'}>
          <ColorIndicator hex={params.row.ColorArray.Color.hex} />
          <Typography variant="inherit">{params.value}</Typography>
        </Stack>
      ),
    },
    {
      field: 'size',
      headerName: 'Size',
      flex: 1,
      valueGetter: (_, row) => row.ApparelSize.labelSize,
    },
    {
      field: 'aetherInventory',
      headerName: 'Inventory',
      flex: 1,
      editable: true,
      type: 'number',
      renderCell: (params) => (
        <Stack direction={'row'} justifyContent={'end'} alignItems={'center'} spacing={1}>
          {params.row.aetherInventory !== params.row.originalInventory && (
            <IconButton size={'small'} onClick={handleReset(params, 'aetherInventory')}>
              <Restore />
            </IconButton>
          )}
          <Typography variant="inherit">{params.value ?? 0}</Typography>
        </Stack>
      ),
    },
    {
      field: 'aetherInventoryMinimum',
      headerName: 'Minimum',
      flex: 1,
      editable: true,
      type: 'number',
      renderCell: (params) => (
        <Stack direction={'row'} justifyContent={'end'} alignItems={'center'} spacing={1}>
          {params.row.aetherInventoryMinimum !== params.row.originalInventoryMinimum && (
            <IconButton size={'small'} onClick={handleReset(params, 'aetherInventoryMinimum')}>
              <Restore />
            </IconButton>
          )}
          <Typography variant="inherit">{params.value ?? 0}</Typography>
        </Stack>
      ),
    },
  ];

  const processRowUpdate = (newRow: InventoryRow, oldRow: InventoryRow) => {
    if ((newRow.aetherInventory ?? 0) < 0 || (newRow.aetherInventoryMinimum ?? 0) < 0) {
      return oldRow;
    }
    onChange(newRow.index, newRow);
    return newRow;
  };

  const handleCellModesModelChange = React.useCallback((newModel: GridCellModesModel) => {
    setCellModesModel(newModel);
  }, []);

  const handleCellClick = React.useCallback((params: GridCellParams, event: React.MouseEvent) => {
    if (!params.isEditable) {
      return;
    }

    // Ignore portal
    if (
      (event.target as any).nodeType === 1 &&
      !event.currentTarget.contains(event.target as Element)
    ) {
      return;
    }

    setCellModesModel((prevModel) => {
      return {
        // Revert the mode of the other cells from other rows
        ...Object.keys(prevModel).reduce(
          (acc, id) => ({
            ...acc,
            [id]: Object.keys(prevModel[id]).reduce(
              (acc2, field) => ({
                ...acc2,
                [field]: { mode: GridCellModes.View },
              }),
              {},
            ),
          }),
          {},
        ),
        [params.id]: {
          // Revert the mode of other cells in the same row
          ...Object.keys(prevModel[params.id] || {}).reduce(
            (acc, field) => ({ ...acc, [field]: { mode: GridCellModes.View } }),
            {},
          ),
          [params.field]: { mode: GridCellModes.Edit },
        },
      };
    });
  }, []);

  return (
    <StyledDataGrid
      apiRef={apiRef}
      rows={parts}
      columns={columns}
      getRowId={(row) => row.index}
      processRowUpdate={processRowUpdate}
      disableRowSelectionOnClick
      getRowClassName={(params) =>
        params.row.aetherInventory !== params.row.originalInventory ||
        params.row.aetherInventoryMinimum !== params.row.originalInventoryMinimum
          ? 'changed'
          : (params.row.aetherInventory ?? 0) < (params.row.aetherInventoryMinimum ?? 0)
          ? 'minimum'
          : ''
      }
      cellModesModel={cellModesModel}
      onCellModesModelChange={handleCellModesModelChange}
      onCellClick={handleCellClick}
      initialState={{
        pagination: { paginationModel: { pageSize: 10 } },
      }}
      autoHeight
      pageSizeOptions={[10, 20, 50]}
    />
  );
}
